import { useEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import toast from "react-hot-toast";
import { Building2, ChevronDown, Search, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useGetChatAudiencesQuery,
  useLazyGetChatAudienceMembersQuery,
} from "@/services/(my-space)/api.chat";
import { getErrorMessage } from "@/utils/error.utils";
import { cn } from "@/utils/cn";
import type {
  ChatAudienceKind,
  IChatAudienceOption,
  IChatAudiences,
} from "@/types/(my-space)/chat.interface";
import { ChatAvatar } from "./ChatAvatar";
import { ContactList } from "./ContactList";
import { personName } from "../_lib/chat-labels";
import {
  COLLAPSE_SELECTION_AT,
  addAudience,
  audienceCount,
  audienceKey,
  newPeople,
  newPeopleCount,
  remainingAudienceOptions,
  audienceTabs,
  filterAudienceOptions,
  removeAudience,
  removePerson,
  togglePerson,
  type AppliedAudience,
  type AudienceTab,
  type PeopleSelection,
} from "../_lib/audience-selection";

type EntityKind = Exclude<ChatAudienceKind, "company">;

const tabLabel = (t: TFunction<"chat">, tab: AudienceTab) => {
  switch (tab) {
    case "people":
      return t("audience.tab_people");
    case "company":
      return t("audience.tab_company");
    case "branch":
      return t("audience.tab_branch");
    case "department":
      return t("audience.tab_department");
    case "team":
      return t("audience.tab_team");
    case "project":
      return t("audience.tab_project");
  }
};

const searchLabel = (t: TFunction<"chat">, kind: EntityKind) => {
  switch (kind) {
    case "branch":
      return t("audience.search_branch");
    case "department":
      return t("audience.search_department");
    case "team":
      return t("audience.search_team");
    case "project":
      return t("audience.search_project");
  }
};

const audienceLabel = (t: TFunction<"chat">, a: AppliedAudience) =>
  a.kind === "company" || !a.names.length
    ? t("audience.company_label")
    : a.names.length === 1
      ? a.names[0]
      : t("audience.chip_more", { name: a.names[0], count: a.names.length - 1 });

/**
 * A group / broadcast / add-members picker: the selection summary, an "Add by"
 * bar (People plus whatever audiences the server offers), and either the
 * contact list or an audience checklist.
 */
export const MemberSelector = ({
  selection,
  setSelection,
  excludeIds,
  error,
  className,
}: {
  selection: PeopleSelection;
  setSelection: Dispatch<SetStateAction<PeopleSelection>>;
  /** Never selectable: me, or the chat's current members. */
  excludeIds: number[];
  /** Rendered under the summary (a validation message). */
  error?: ReactNode;
  className?: string;
}) => {
  const { t } = useTranslation("chat");
  const [tab, setTab] = useState<AudienceTab>("people");
  const audiences = useGetChatAudiencesQuery();
  const tabs = audienceTabs(audiences.data, selection, excludeIds);
  const activeTab = tabs.includes(tab) ? tab : "people";
  const selectedIds = useMemo(
    () => new Set(selection.people.map((p) => p.id)),
    [selection.people],
  );

  // The request resolves after an await; merge into what is selected *then*,
  // not into the render that started it (a chip removed meanwhile stays removed).
  const latest = useRef(selection);
  useEffect(() => {
    latest.current = selection;
  }, [selection]);

  const applied = (audience: AppliedAudience, members: Parameters<typeof addAudience>[2]) => {
    // Outside an updater so the toast fires once (StrictMode runs updaters twice).
    const { selection: next, added } = addAudience(latest.current, audience, members, excludeIds);
    latest.current = next;
    setSelection(next);
    if (added > 0) toast.success(t("audience.added", { count: added }));
    else toast(t("audience.added_none"));
  };

  return (
    <div className={cn("flex min-h-0 flex-col gap-4", className)}>
      <SelectionSummary
        selection={selection}
        onRemovePerson={(id) => setSelection((s) => removePerson(s, id))}
        onRemoveAudience={(key) => setSelection((s) => removeAudience(s, key))}
      />
      {error}
      {tabs.length > 1 ? (
        <Tabs value={activeTab} onValueChange={(v) => setTab(v as AudienceTab)} className="shrink-0">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              {t("audience.add_by")}
            </span>
            <TabsList
              aria-label={t("audience.add_by")}
              className="h-auto w-full flex-wrap justify-start gap-0.5"
            >
              {tabs.map((tb) => (
                <TabsTrigger key={tb} value={tb} className="px-3 py-1.5 text-sm">
                  {tabLabel(t, tb)}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        </Tabs>
      ) : null}
      {/* Kept mounted so its search and scroll survive a trip to another tab. */}
      <ContactList
        className={cn("flex-1", activeTab !== "people" && "hidden")}
        excludeIds={excludeIds}
        selectedIds={selectedIds}
        onPick={(c) => setSelection((s) => togglePerson(s, c))}
      />
      {activeTab !== "people" && audiences.data ? (
        <AudiencePanel
          key={activeTab}
          kind={activeTab}
          audiences={audiences.data}
          selection={selection}
          excludeIds={excludeIds}
          onApplied={applied}
        />
      ) : null}
    </div>
  );
};

/** Entities with counts, checked then resolved to people in one request. */
const AudiencePanel = ({
  kind,
  audiences,
  selection,
  excludeIds,
  onApplied,
}: {
  kind: ChatAudienceKind;
  audiences: IChatAudiences;
  selection: PeopleSelection;
  excludeIds: number[];
  onApplied: (audience: AppliedAudience, members: Parameters<typeof addAudience>[2]) => void;
}) => {
  const { t } = useTranslation("chat");
  const [search, setSearch] = useState("");
  const [checked, setChecked] = useState<Set<number>>(() => new Set());
  const [resolve, { isFetching }] = useLazyGetChatAudienceMembersQuery();

  const options = useMemo(
    () =>
      kind === "company" ? [] : remainingAudienceOptions(audiences, kind, selection, excludeIds),
    [audiences, kind, selection, excludeIds],
  );
  const visible = useMemo(() => filterAudienceOptions(options, search), [options, search]);
  const chosen = options.filter((o) => checked.has(o.id));
  // Counts are who it would ADD, not the entity's size.
  const companyCount = audiences.company?.memberIds?.length
    ? newPeople(audiences.company.memberIds, selection, excludeIds).length
    : (audiences.company?.memberCount ?? 0);
  const upTo =
    kind === "company" ? companyCount : newPeopleCount(chosen, selection, excludeIds);
  const canApply = kind === "company" ? companyCount > 0 : chosen.length > 0;

  const toggle = (o: IChatAudienceOption) =>
    setChecked((set) => {
      const next = new Set(set);
      if (next.has(o.id)) next.delete(o.id);
      else next.add(o.id);
      return next;
    });

  const apply = async () => {
    if (!canApply || isFetching) return;
    const ids = chosen.map((o) => o.id);
    try {
      const res = await resolve(
        kind === "company" ? { kind } : { kind, ids },
        false,
      ).unwrap();
      onApplied(
        { key: audienceKey(kind, ids), kind, names: chosen.map((o) => o.name) },
        res.members,
      );
      setChecked(new Set());
    } catch (e) {
      toast.error(getErrorMessage(e, t("errors.audience_members")));
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2" aria-busy={isFetching}>
      {kind === "company" ? (
        <div className="flex items-center gap-3 rounded-sm border border-border bg-muted/40 p-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary-muted text-primary">
            <Building2 className="size-4" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium text-foreground">
              {t("audience.everyone", { n: companyCount })}
            </span>
            <span className="text-xs text-muted-foreground">{t("audience.everyone_hint")}</span>
          </div>
        </div>
      ) : (
        <>
          <div className="relative shrink-0">
            <Search className="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchLabel(t, kind)}
              aria-label={searchLabel(t, kind)}
              className="h-10 ps-9"
            />
          </div>
          <ul className="min-h-0 flex-1 overflow-y-auto">
            {visible.length === 0 ? (
              <li className="py-8 text-center text-sm text-muted-foreground">
                {t("audience.no_matches")}
              </li>
            ) : (
              visible.map((o) => {
                const id = `chat-audience-${kind}-${o.id}`;
                return (
                  <li key={o.id}>
                    <label
                      htmlFor={id}
                      className={cn(
                        "flex w-full cursor-pointer items-center gap-3 rounded-sm px-2 py-2.5 hover:bg-muted",
                        checked.has(o.id) && "bg-primary/5",
                        o.memberCount === 0 && "cursor-not-allowed opacity-60",
                      )}
                    >
                      <Checkbox
                        id={id}
                        checked={checked.has(o.id)}
                        disabled={o.memberCount === 0 || isFetching}
                        onCheckedChange={() => toggle(o)}
                      />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                        {o.name}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {t("audience.member_count", { count: o.memberCount })}
                      </span>
                    </label>
                  </li>
                );
              })
            )}
          </ul>
        </>
      )}
      <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border pt-2">
        <span className="text-xs text-muted-foreground">
          {canApply ? t("audience.up_to", { count: upTo }) : null}
        </span>
        <Button
          variant="outline-primary"
          size="sm"
          disabled={!canApply}
          isLoading={isFetching}
          onClick={() => void apply()}
        >
          {t("audience.apply")}
        </Button>
      </div>
    </div>
  );
};

const PAGE = 100;

/**
 * Audience chips (undo) then person chips. Past COLLAPSE_SELECTION_AT people
 * it collapses to a count, and the expanded list renders a page at a time —
 * a whole company would otherwise be 2000 chips on every keystroke.
 */
const SelectionSummary = ({
  selection,
  onRemovePerson,
  onRemoveAudience,
}: {
  selection: PeopleSelection;
  onRemovePerson: (id: number) => void;
  onRemoveAudience: (key: string) => void;
}) => {
  const { t } = useTranslation("chat");
  const [expanded, setExpanded] = useState(false);
  const [shown, setShown] = useState(PAGE);
  const { people, audiences } = selection;
  if (!people.length) return null;
  const collapsed = people.length > COLLAPSE_SELECTION_AT;
  const listVisible = !collapsed || expanded;
  const page = collapsed ? people.slice(0, shown) : people;

  return (
    <div className="flex shrink-0 flex-col gap-1.5">
      {audiences.length ? (
        <div className="flex flex-wrap gap-1.5">
          {audiences.map((a) => {
            const label = audienceLabel(t, a);
            return (
              <span
                key={a.key}
                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-primary/30 bg-primary-muted py-0.5 ps-2 pe-2 text-xs font-medium text-primary"
              >
                <Users className="size-3 shrink-0" />
                <span className="truncate">
                  {t("audience.chip", { label, count: audienceCount(selection, a.key) })}
                </span>
                <button
                  type="button"
                  onClick={() => onRemoveAudience(a.key)}
                  aria-label={t("audience.remove", { name: label })}
                  className="shrink-0 text-primary/70 hover:text-primary"
                >
                  <X className="size-3" />
                </button>
              </span>
            );
          })}
        </div>
      ) : null}
      {collapsed ? (
        <div className="flex items-center justify-between gap-2 rounded-sm bg-muted/60 px-2 py-1">
          <span className="text-xs font-medium text-foreground">
            {t("audience.people_selected", { count: people.length })}
          </span>
          <Button
            variant="ghost"
            size="xs"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? t("audience.hide_list") : t("audience.show_list")}
            <ChevronDown className={cn("size-3.5 transition-transform", expanded && "rotate-180")} />
          </Button>
        </div>
      ) : null}
      {listVisible ? (
        <div className={cn("flex flex-wrap gap-1.5 overflow-y-auto", collapsed ? "max-h-36" : "max-h-24")}>
          {page.map((p) => (
            <span
              key={p.id}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 py-0.5 ps-0.5 pe-2 text-xs text-foreground"
            >
              <ChatAvatar name={personName(t, p)} imageUrl={p.profileImage} size="xs" />
              {personName(t, p)}
              <button
                type="button"
                onClick={() => onRemovePerson(p.id)}
                aria-label={t("audience.remove", { name: personName(t, p) })}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
          {collapsed && people.length > shown ? (
            <Button variant="link" size="xs" onClick={() => setShown((n) => n + PAGE)}>
              {t("audience.show_more", { count: Math.min(PAGE, people.length - shown) })}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
